import { useMemo, useSyncExternalStore } from 'react';
import {
  BreakpointName,
  BREAKPOINTS,
  createMediaQuery,
  Responsive,
} from './createMediaQuery';
import { requireNonNull } from './requireNonNull';

/**
 * The active step among the configured ones, as an external store. The
 * snapshot is the step NAME, not the config value: callers pass a fresh config
 * object every render, and a fresh value would re-render without end.
 */
function breakpointStore(steps: BreakpointName[]) {
  const configured = BREAKPOINTS.filter((x) => steps.includes(x.name));
  // Server HTML and hydration: the widest step — the viewport is not known there.
  const widest = configured[0].name;

  let lists: Array<{ name: BreakpointName; list: MediaQueryList }> | undefined;
  const mediaLists = () =>
    (lists ??= configured.flatMap(({ name, min }) =>
      min == null
        ? []
        : [{ name, list: window.matchMedia(createMediaQuery(min, null)) }],
    ));

  return {
    subscribe: (onChange: () => void) => {
      if (!window.matchMedia) return () => {};
      mediaLists().forEach(({ list }) =>
        list.addEventListener('change', onChange),
      );
      return () =>
        mediaLists().forEach(({ list }) =>
          list.removeEventListener('change', onChange),
        );
    },
    getSnapshot: (): BreakpointName => {
      if (!window.matchMedia) return widest;
      return mediaLists().find(({ list }) => list.matches)?.name ?? '_';
    },
    getServerSnapshot: () => widest,
  };
}

// Always require _ to avoid an unwanted 'undefined' return type
export function useBreakpoint<T>(config: Responsive<T> & { _: T }): T {
  const key = BREAKPOINTS.filter((x) => config[x.name] != null)
    .map((x) => x.name)
    .join(' ');
  const store = useMemo(
    () => breakpointStore(key.split(' ') as BreakpointName[]),
    [key],
  );
  const name = useSyncExternalStore(
    store.subscribe,
    store.getSnapshot,
    store.getServerSnapshot,
  );

  return requireNonNull(config[name]);
}
